import React, { useEffect, useMemo, useState } from 'react';
import { LabExam } from '../../LabExam';
import { formatDate } from '../../utils/dateUtils';
import './Lab.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useColumnDrag } from '../../hooks/useColumnDrag';
import ColumnFilter from '../Filters/ColumnFilter';
import { useResizableColumns } from '../../hooks/useResizableColumns';

const exportToExcel = (data: LabExam[], visibleColumns: string[], weldedJointsOnly = false) => {
	const columnMapping: { [key: string]: string } = {
		state_company: 'Округ',
		region_company: 'Регион',
		address_company: 'Адрес производителя',
		name_company: 'Производитель',
		inn_company: 'ИНН производителя',
		exam_date: 'Дата фиксации',
		object_name: 'Наименование объекта',
		geomaps: 'Координаты',
		object_adres: 'Адрес объекта',
		reason_request: 'Основание обращения',
		exploitation_time: 'Срок эксплуатации',
		customer: 'Заказчик',
		product_name: 'Наименование продукции',
		technical_documentation: 'Техническая документация',
		compliance_requirements: 'Соответствие требованиям',
		exam_result: 'Результат испытания',
		welding_joint_type: 'Тип сварного соединения',
	};

	const formattedData = data.map(item => {
		const row: { [key: string]: any } = {};
		visibleColumns.forEach(key => {
			if (key === 'manufacture') {
				return;
			}
			
			const label = columnMapping[key];
			if (!label) return;
			if (key === 'exam_date') {
				row[label] = formatDate(item.exam_date);
			} else {
				row[label] = (item as any)[key] ?? '';
			}
		});
		return row;
	});

	const sheetName = weldedJointsOnly ? 'Сварные соединения' : 'Лабораторные испытания';
	const worksheet = XLSX.utils.json_to_sheet(formattedData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

	const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
	const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
	saveAs(dataBlob, weldedJointsOnly ? 'Реестр испытаний сварных соединений.xlsx' : 'Лабораторные испытания.xlsx');
};

const deduplicateLabData = (data: LabExam[]): LabExam[] => {
    //Удаляются только полные дубликаты
    const seen = new Set<string>();
    return data.filter((item) => {
        const key = JSON.stringify({
            state_company: item.state_company,
            region_company: item.region_company,
            address_company: item.address_company,
            name_company: item.name_company,
            inn_company: item.inn_company,
            exam_date: item.exam_date,
            object_name: item.object_name,
            geomaps: item.geomaps,
            object_adres: item.object_adres,
            reason_request: item.reason_request,
            exploitation_time: item.exploitation_time,
            customer: item.customer,
            product_name: item.product_name,
            technical_documentation: item.technical_documentation,
            compliance_requirements: item.compliance_requirements,
            exam_result: item.exam_result,
            welding_joint_type: item.welding_joint_type,
        });
        
        if (seen.has(key)) {
            return false; 
        }
        
        seen.add(key);
        return true;
    });
};

interface DetailInfoProps {
	currentItems: LabExam[];
	sortField: keyof LabExam | null;
	sortDirection: 'asc' | 'desc' | null;
	onSort: (field: keyof LabExam) => void;
	onFilterToggle?: () => void;
    onRowClick?: (lab: LabExam) => void;
	weldedJointsOnly?: boolean;
}

function DetailInfo({ currentItems, sortField, sortDirection, onSort, onFilterToggle, onRowClick, weldedJointsOnly = false }: DetailInfoProps) {
    const uniqueCurrentItems = useMemo(() => deduplicateLabData(currentItems), [currentItems]);
	const labColumnOrder = [
		'state_company',
		'region_company',
		'address_company',
		'name_company',
		'inn_company',
		'exam_date',
		'object_name',
		'geomaps',
		'object_adres',
		'reason_request',
		'exploitation_time',
		'customer',
		'product_name',
		'technical_documentation',
		'compliance_requirements',
		'exam_result',
	];
	const defaultColumnOrder = weldedJointsOnly
		? [...labColumnOrder, 'welding_joint_type']
		: labColumnOrder;
	const columnStorageKey = weldedJointsOnly ? 'welded-joint-column-order' : 'lab-exam-column-order';
	const visibleStorageKey = weldedJointsOnly ? 'welded-joint-visible-columns' : 'lab-exam-visible-columns';

    const {
        columnOrder,
        draggedColumn,
        handleDragStart,
        handleDragOver,
        handleDrop,
        handleDragEnd
    } = useColumnDrag({
        defaultColumnOrder,
        storageKey: columnStorageKey
    });

	const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
		const saved = localStorage.getItem(visibleStorageKey);
		if (saved) {
			const parsed = JSON.parse(saved);
			const columns = parsed.filter((col: string) => col !== 'manufacture');
			if (weldedJointsOnly && !columns.includes('welding_joint_type')) columns.push('welding_joint_type');
			return columns;
		}
		localStorage.setItem(visibleStorageKey, JSON.stringify(defaultColumnOrder));
		return defaultColumnOrder;
	});

	useEffect(() => {
		localStorage.setItem(visibleStorageKey, JSON.stringify(visibleColumns));
	}, [visibleColumns, visibleStorageKey]);

	const filteredColumnOrder = useMemo(
		() => columnOrder.filter(col =>
			visibleColumns.includes(col)
			&& col !== 'manufacture'
			&& (weldedJointsOnly || col !== 'welding_joint_type')
		),
		[columnOrder, visibleColumns, weldedJointsOnly]
	);

    const columns = [
        { key: 'state_company', label: 'Округ', editable: false },
        { key: 'region_company', label: 'Регион', editable: false },
        { key: 'address_company', label: 'Адрес производителя', editable: false },
        { key: 'name_company', label: 'Производитель', editable: false },
        { key: 'inn_company', label: 'ИНН производителя', editable: false },
        { key: 'exam_date', label: 'Дата фиксации', editable: true },
        { key: 'object_name', label: 'Наименование объекта', editable: true },
        { key: 'geomaps', label: 'Координаты', editable: true },
        { key: 'object_adres', label: 'Адрес объекта', editable: true },
        { key: 'reason_request', label: 'Основание обращения', editable: true },
        { key: 'exploitation_time', label: 'Срок эксплуатации', editable: true },
        { key: 'customer', label: 'Заказчик', editable: true },
        { key: 'product_name', label: 'Наименование продукции', editable: true },
        { key: 'technical_documentation', label: 'Техническая документация', editable: true },
        { key: 'compliance_requirements', label: 'Соответствие требованиям', editable: true },
        { key: 'exam_result', label: 'Результат испытания', editable: true },
        { key: 'welding_joint_type', label: 'Тип сварного соединения', editable: false },
    ];

    const getSortIcon = (field: keyof LabExam) => {
        if (field !== sortField) return '↕';
        if (sortDirection === 'asc') return '↑';
        if (sortDirection === 'desc') return '↓';
        return '↕';
    };

    const getColumnByKey = (key: string) => columns.find(col => col.key === key);

	const renderCell = (item: LabExam, columnKey: string) => {
		if (columnKey === 'manufacture') {
			return '';
		}
		
		switch (columnKey) {
			case 'exam_date':
				return formatDate(item.exam_date);
			default:
				return String((item as any)[columnKey] ?? '');
		}
	};

    const { getStyleForColumn, handleMouseDown, resetColumnWidths } = useResizableColumns(filteredColumnOrder, {
        storageKey: weldedJointsOnly ? 'welded-joint-column-widths' : 'lab-exam-column-widths',
        minWidthPx: 100,
        maxWidthPx: 800,
    });
	const weldedColumnMinWidths: Record<string, number> = {
		object_name: 160,
		product_name: 210,
		technical_documentation: 170,
		compliance_requirements: 210,
		exam_result: 160,
		welding_joint_type: 210,
	};
	const getColumnStyle = (columnKey: string): React.CSSProperties => {
		const savedStyle = getStyleForColumn(columnKey);
		if (!weldedJointsOnly) return savedStyle;
		const minWidth = weldedColumnMinWidths[columnKey] ?? 100;
		const savedWidth = typeof savedStyle.width === 'number' ? savedStyle.width : minWidth;
		return { ...savedStyle, width: Math.max(savedWidth, minWidth), minWidth, maxWidth: undefined };
	};

    return (
        <div className={`table-container${weldedJointsOnly ? ' welded-joint-table-container' : ''}`}>
            <div className="table-controls">
                <button onClick={() => exportToExcel(currentItems, filteredColumnOrder, weldedJointsOnly)} className="export-button">
                Экспортировать в Excel
                </button>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {onFilterToggle && (
                        <button 
                            onClick={onFilterToggle}
                            style={{
                                background: 'none',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3"/>
                            </svg>
                            Фильтр
                        </button>
                    )}
                    <ColumnFilter
                        columns={(weldedJointsOnly ? columns : columns.filter(column => column.key !== 'welding_joint_type'))
                            .map(column => ({ key: column.key, label: column.label }))}
                        visibleColumns={visibleColumns}
                        onColumnVisibilityChange={setVisibleColumns}
                        defaultColumns={defaultColumnOrder}
                    />
                </div>
            </div>

            <table className={`lab-table${weldedJointsOnly ? ' welded-joint-lab-table' : ''}`}>
                <thead>        
                    <tr className='table-header_detail'>
                        {filteredColumnOrder.map((columnKey) => {
                            const column = getColumnByKey(columnKey);
                            if (!column) return null;

                            return (
                                <th 
                                    key={columnKey}
                                    className='table-header-item' 
                                    onClick={() => onSort(column.key as keyof LabExam)} 
                                    style={{ 
                                        padding: '4px',
                                        cursor: 'grab',
                                        backgroundColor: draggedColumn === columnKey ? '#005a94' : undefined,
                                        opacity: draggedColumn && draggedColumn !== columnKey ? 0.7 : 1,
                                        ...getColumnStyle(columnKey)
                                    }}
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, columnKey)}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, columnKey)}
                                    onDragEnd={handleDragEnd}
                                >
                                    {column.label} {getSortIcon(column.key as keyof LabExam)}
                                    <span className="col-resizer" onMouseDown={(e) => handleMouseDown(columnKey, e)} />
                                </th>
                            );
                        })}
                      
                    </tr>
                </thead>
                <tbody>
                    {uniqueCurrentItems.map((lab, rowIndex) => (
                        <tr
                            className={`table-body_detail${onRowClick ? ' table-row-clickable' : ''}`}
                            key={`${lab.name_company}-${lab.inn_company}-${lab.object_name}-${lab.exam_date}-${rowIndex}`}
                            onClick={() => onRowClick?.(lab)}
                            onKeyDown={(event) => {
                                if (!onRowClick) return;
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onRowClick(lab);
                                }
                            }}
                            tabIndex={onRowClick ? 0 : -1}
                            role={onRowClick ? 'button' : undefined}
                            aria-label={
                                onRowClick
                                    ? `Подробнее об испытании ${lab.product_name || ''} от ${formatDate(lab.exam_date)}`
                                    : undefined
                            }
                        >
                            {filteredColumnOrder.map((columnKey) => (
                                <td key={columnKey} className='table-body-item' style={{ ...getColumnStyle(columnKey) }}>
                                    {renderCell(lab, columnKey)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default DetailInfo;
